/**
 * 地震発生位置種別を表すEnum。
 */
export const enum EqLocationTypeId {
    /** 内陸地殻内 */
    Crustal = 1,

    /** プレート間 */
    Interplate = 2,

    /** プレート内 */
    Intraplate = 3,

    /** 不明 */
    Unknown = 99,
}

const eqLocationTypeIdDef = {
    [EqLocationTypeId.Crustal]: "内陸地殻内",
    [EqLocationTypeId.Interplate]: "プレート間",
    [EqLocationTypeId.Intraplate]: "プレート内",
    [EqLocationTypeId.Unknown]: "不明",
};

export const allEqLocationTypeIds = [
    EqLocationTypeId.Crustal,
    EqLocationTypeId.Interplate,
    EqLocationTypeId.Intraplate,
    EqLocationTypeId.Unknown,
] as const;

/**
 * 指定された地震発生位置種別に対応する表示用文字列を返す。
 *
 * @param val 地震発生位置種別
 * @returns 表示用文字列
 */
export const eqLocationTypeIdToString = (val: EqLocationTypeId) => eqLocationTypeIdDef[val];

/**
 * メカニズム種別を示すEnum。
 */
export const enum EqMechanismTypeId {
    /** 横ずれ (下記以外) */
    StrikeSlip = 1,

    /** 逆断層 (45<λ<135) */
    ReverseFault = 2,

    /** 正断層 (-135<λ<-45) */
    NormalFault = 3,

    /** 不明 */
    Unknown = 99,
}

const eqMechanismTypeIdDef = {
    [EqMechanismTypeId.StrikeSlip]: "横ずれ",
    [EqMechanismTypeId.ReverseFault]: "逆断層 (45 < λ < 135)",
    [EqMechanismTypeId.NormalFault]: "正断層 (-135 < λ < -45)",
    [EqMechanismTypeId.Unknown]: "不明",
} as const;

export const allEqMechanismTypeIds = [
    EqMechanismTypeId.StrikeSlip,
    EqMechanismTypeId.ReverseFault,
    EqMechanismTypeId.NormalFault,
    EqMechanismTypeId.Unknown,
] as const;

/**
 * 指定されたメカニズム種別IDに対応する表示用文字列を返す。
 *
 * @param val メカニズム種別
 * @returns 表示用文字列
 */
export const eqMechanismTypeIdToString = (val: EqMechanismTypeId) => eqMechanismTypeIdDef[val];

export const enum EqLocationTypeIdSource {
    /** 沈み込むプレート形状モデル、震源位置による */
    PlateModelHypocenter = 1,

    /** 地震調査研究推進本部による */
    HERP = 2,

    /** 沈み込むプレート形状モデル、震源位置、震源メカニズム解による */
    PlateModelHypocenterFocal = 3,
}

const eqLocationTypeIdSourceDef = {
    [EqLocationTypeIdSource.PlateModelHypocenter]: "沈み込むプレート形状モデル、震源位置による",
    [EqLocationTypeIdSource.HERP]: "地震調査研究推進本部による",
    [EqLocationTypeIdSource.PlateModelHypocenterFocal]: "沈み込むプレート形状モデル、震源位置、震源メカニズム解による",
} as const;

export const allEqLocationTypeIdSources = [
    EqLocationTypeIdSource.PlateModelHypocenter,
    EqLocationTypeIdSource.HERP,
    EqLocationTypeIdSource.PlateModelHypocenterFocal,
] as const;

/**
 * 指定された地震発生位置種別ソースに対応する表示用文字列を返す。
 *
 * @param val 地震発生位置種別ソース
 * @returns 表示用文字列
 */
export const eqLocationTypeIdSourceToString = (val: EqLocationTypeIdSource | null | undefined) => {
    if (val == null) {
        return "不明";
    }

    return eqLocationTypeIdSourceDef[val];
};

/**
 * 観測網を示すEnum。
 */
export const enum ObsNetworkId {
    KNet = 1,
    KiKnet = 2,
}

const obsNetworkIdDef = {
    [ObsNetworkId.KNet]: "K-NET",
    [ObsNetworkId.KiKnet]: "KiK-net",
};

export const allObsNetworkIds = [
    ObsNetworkId.KNet,
    ObsNetworkId.KiKnet,
] as const;

/**
 * 指定された観測網に対応する表示用文字列を返す。
 *
 * @param val 観測網
 * @returns 表示用文字列
 */
export const obsNetworkIdToString = (val: ObsNetworkId) => obsNetworkIdDef[val];

/**
 * 設置場所を示すEnum。
 */
export const enum InstallationSituationId {
    /** 地表 */
    Ground = 1,

    /** 地中 */
    Borehole = 2,

    /** 建物内 */
    InHouse = 3,

    /** 海底 */
    OceanBottom = 4,

    /** その他 */
    Other = 10,

    /** 不明 */
    Unknown = 99,
}

const installationSituationIdDef = {
    [InstallationSituationId.Ground]: "地表",
    [InstallationSituationId.Borehole]: "地中",
    [InstallationSituationId.InHouse]: "建物内",
    [InstallationSituationId.OceanBottom]: "海底",
    [InstallationSituationId.Other]: "その他",
    [InstallationSituationId.Unknown]: "不明",
};

export const installationSituationIds = [
    InstallationSituationId.Ground,
    InstallationSituationId.Borehole,
    InstallationSituationId.InHouse,
    InstallationSituationId.OceanBottom,
    InstallationSituationId.Other,
    InstallationSituationId.Unknown,
];

/**
 * 指定された設置場所に対応する表示用文字列を返す。
 *
 * @param val 設置場所
 * @returns 表示用文字列
 */
export const installationSituationIdToString = (val: InstallationSituationId) => installationSituationIdDef[val];
